import {CssVarsProviderConfig} from "@mui/system";
import {SupportedColorScheme} from "@mui/material/styles/experimental_extendTheme";
import {CssVarsThemeOptions} from "@mui/material/styles";
import React, {createContext, FunctionComponent, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Experimental_CssVarsProvider as CssVarsProvider} from "@mui/material/styles/CssVarsProvider";
import {CssBaseline, CssVarsTheme} from "@mui/material";
import {ApiResponse, BroadcastService, CachingService, EnvService, PartnerColorSchemeBroadcastArg} from "../../../core";
import BizkeyTechTheme, {GetBizkeytechDefaultThemeArg} from "../../theme";

interface FetchPartnerColorScheme {
    PrimaryColor?: string;
    SecondaryColor?: string;
    primaryColor?: string;
    secondaryColor?: string;
}

interface IThemeProviderContext {
    fetchPartnerColorScheme?: (authorizationToken: string, abortController: AbortController) => ApiResponse<FetchPartnerColorScheme | undefined>;
}

const CssVarsThemeContext = createContext<CssVarsTheme>(BizkeyTechTheme.combineTheme());
const ThemeProviderContext = createContext<IThemeProviderContext>({
    fetchPartnerColorScheme: undefined
});

export interface ThemeColorSchemes {
    primaryColor?: string;
    secondaryColor?: string;
}

export interface ThemeProviderProps extends Omit<CssVarsProviderConfig<SupportedColorScheme>, 'theme' | 'defaultColorScheme'> {
    authorizationToken?: string;
    excludeCssBaseline?: boolean;
    excludePartnerColorScheme?: boolean;
    colorScheme?: ThemeColorSchemes;
    themePrefix?: string;
    themes?: CssVarsThemeOptions[];
    fetchPartnerColorScheme?: IThemeProviderContext['fetchPartnerColorScheme'];
}

const ThemeProvider: FunctionComponent<PropsWithChildren<ThemeProviderProps>> = (props) => {
    const {authorizationToken, excludeCssBaseline, excludePartnerColorScheme, fetchPartnerColorScheme, ...cssProviderProps} = props;
    const {fetchPartnerColorScheme: fetchPartnerColorSchemeFromContext} = useContext(ThemeProviderContext);

    const [colorScheme, setColorScheme] = useState<ThemeColorSchemes | undefined>(props.colorScheme ?? (!props.excludePartnerColorScheme ? CachingService.getPartnerColorScheme() : undefined));
    const getPartnerColorScheme = usePartnerColorScheme(props.authorizationToken!, fetchPartnerColorScheme ?? fetchPartnerColorSchemeFromContext);

    const combinedTheme = useMemo(() => {
        const options: GetBizkeytechDefaultThemeArg = {
            primaryColor: colorScheme?.primaryColor,
            secondaryColor: colorScheme?.secondaryColor,
            themePrefix: props.themePrefix,
        }
        return BizkeyTechTheme.combineTheme(options, ...(props.themes ?? []));
    }, [colorScheme?.primaryColor, colorScheme?.secondaryColor, props.themePrefix, props.themes]);

    /**
     * With each change in the color scheme:
     * - Sets the color scheme and applies it to the theme.
     */
    useEffect(() => {
        setColorScheme(props.colorScheme ?? (!props.excludePartnerColorScheme ? CachingService.getPartnerColorScheme() : undefined));
    }, [props.colorScheme, props.excludePartnerColorScheme])

    /**
     * As soon as the component is mounted and the authorization token is present:
     * - Fetches the partner color scheme from the server.
     */
    useEffect(() => {
        if (!props.authorizationToken || props.excludePartnerColorScheme)
            return;
        const abortController = new AbortController();
        void getPartnerColorScheme(abortController);
        return () => abortController.abort();
    }, [getPartnerColorScheme, props.authorizationToken, props.excludePartnerColorScheme]);

    /**
     * As soon as the component is mounted,
     * - Adds an event listener to the broadcast channel 'partnerColorScheme' for the event 'message'.
     */
    useEffect(() => {
        if (props.excludePartnerColorScheme)
            return;
        BroadcastService.addEventListener<PartnerColorSchemeBroadcastArg>(
            BroadcastService.channelNames.partnerColorScheme,
            'message',
            (arg) => {
                if (arg.type === 'color-scheme') {
                    setColorScheme(arg.payload);
                    CachingService.setPartnerColorScheme(arg.payload);
                }
            }
        )
    }, [props.excludePartnerColorScheme]);

    const themeProviderContextValue = useMemo<IThemeProviderContext>(() => ({
        fetchPartnerColorScheme: fetchPartnerColorScheme,
    }), [fetchPartnerColorScheme])

    return (
        <>
            <CssVarsProvider {...cssProviderProps} theme={combinedTheme}>
                <CssVarsThemeContext.Provider value={combinedTheme}>
                    <ThemeProviderContext.Provider value={themeProviderContextValue}>
                        {
                            !props.excludeCssBaseline &&
                            <CssBaseline enableColorScheme/>
                        }
                        {props.children}
                    </ThemeProviderContext.Provider>
                </CssVarsThemeContext.Provider>
            </CssVarsProvider>
        </>
    );
}

/**
 * A convenient wrapper to fetch the css vars theme value of the context.
 */
export const useCssVarsTheme = (): CssVarsTheme => {
    return useContext(CssVarsThemeContext);
}


export interface UsePartnerColorScheme {
    (authorizationToken: string, fetchPartnerColorScheme?: IThemeProviderContext['fetchPartnerColorScheme']): (abortController: AbortController) => Promise<boolean>;
}

/**
 * A hook that fetches the partner color scheme from the server.
 * @param authorizationToken                The authorization token that is used to authenticate the request.
 * @param fetchPartnerColorSchemeProps      The function that fetches the partner color scheme from the server.
 */
export const usePartnerColorScheme: UsePartnerColorScheme = (authorizationToken, fetchPartnerColorSchemeProps) => {
    const {fetchPartnerColorScheme: fetchPartnerColorSchemeFromContext} = useContext(ThemeProviderContext);
    const fetchPartnerColorScheme = useMemo(() => fetchPartnerColorSchemeFromContext ?? fetchPartnerColorSchemeProps, [fetchPartnerColorSchemeProps, fetchPartnerColorSchemeFromContext]);

    /**
     * Fetches the partner color scheme from the server.
     * @param abortController   The abort controller that can be used to cancel the request.
     */
    return useCallback(async (abortController: AbortController): Promise<boolean> => {
        let response: PromiseType<ReturnType<Required<IThemeProviderContext>['fetchPartnerColorScheme']>> = undefined;
        if (fetchPartnerColorScheme) {
            response = await fetchPartnerColorScheme(authorizationToken, abortController);
        }
        if (response?.resultFlag) {
            BroadcastService.postMessage<PartnerColorSchemeBroadcastArg>(
                BroadcastService.channelNames.partnerColorScheme,
                {
                    type: 'color-scheme',
                    payload: {
                        primaryColor: response?.data?.PrimaryColor ?? response?.data?.primaryColor,
                        secondaryColor: response?.data?.SecondaryColor ?? response?.data?.secondaryColor,
                    }
                }
            );
        } else {
            BroadcastService.postMessage<PartnerColorSchemeBroadcastArg>(
                BroadcastService.channelNames.partnerColorScheme,
                {
                    type: 'color-scheme',
                    payload: {
                        primaryColor: EnvService.primaryColor,
                        secondaryColor: EnvService.secondaryColor,
                    }
                }
            );
        }
        return true;
    }, [authorizationToken, fetchPartnerColorScheme])
}


export default ThemeProvider;
